import { Injectable } from '@angular/core';
import {
  ApplicantFieldValue,
  FieldValue,
  Form,
  FormElements,
  RegistrantFieldValue,
  SaveRequest,
  SaveResponse
} from "../interfaces/form-model";
import { Observable } from "rxjs";
import { HttpClient } from "@angular/common/http";

type ReactiveFieldValues = { [key: string]: any };

@Injectable({
  providedIn: 'root'
})
export class FormSaveService {

  constructor(private http: HttpClient) { }

  save(id: string, requestBody: any): Observable<SaveResponse> {
    return this.http.put<SaveResponse>(`${window.location.origin}/ui-forms/v1/form/submit/${id}`, requestBody);
  }

  /**
   * Creates the SaveRequest object which is specific formatting for the field values from the Form data and Reactive Forms value objects.
   * It takes the original Form data to transform the final SaveRequest object for the server. The SaveRequest contains the fields that changed from the last time
   * the form was submitted.
   *
   * The initial Reactive Form and current Reactive Form objects which are complex objects with key/value pair mappings. We compare the differences
   * as the final request object for the server only cares about field ids and their selected values for fields that have changed.
   *
   * @param originalForm
   * @param initialReactiveForm
   * @param currentReactiveForm
   */
  createSubmitRequest(originalForm: Form, initialReactiveForm: ReactiveFieldValues, currentReactiveForm: ReactiveFieldValues): SaveRequest {

    const diffedValues: ReactiveFieldValues = this.computeDiffFromPreviousFormAndCurrent(initialReactiveForm, currentReactiveForm);
    const partialRequest: Partial<SaveRequest> = {}
    
    const applicantFieldValues = this.transformApplicantFieldValues(originalForm, diffedValues);
    if (applicantFieldValues.length) partialRequest.applicants = applicantFieldValues;
  
    const registrantFieldValues = this.transformRegistrantsFieldValues(originalForm, diffedValues);
    if (registrantFieldValues.length) partialRequest.registrants = registrantFieldValues;

    // Transform Organization field values. If there are Organization values, we add it to the organization object as field values
    const organizationFieldValues = this.transformOrganizationFieldValues(originalForm, diffedValues);
    if (organizationFieldValues.length) partialRequest.organization = { fieldValues: organizationFieldValues }

    const fieldValues = this.transformFieldValues(originalForm, diffedValues);
    if (fieldValues.length) partialRequest.fieldValues = fieldValues;

    return partialRequest;
  }

  private transformRegistrantsFieldValues(originalForm: Form, diffedValues: ReactiveFieldValues): RegistrantFieldValue[] {

    const key = Object.keys(diffedValues)[0];
    const splitKey = key.split("_")
    const changedId = parseInt(splitKey[1]);
    const result: RegistrantFieldValue[] = [];

    for (const section of originalForm.sections) {
      if (!section.selectedRegistrants) continue;

      for (const registrant of section.selectedRegistrants) {
        if (registrant.id !== changedId) continue;

        const values = this.filterTransformElement(registrant.elements, diffedValues);
        if (values.length) {
          result.push({ id: registrant.id, sectionId: section.key, fieldValues: values });
        }
      }
    }
    return result;
  }

  private transformOrganizationFieldValues(originalForm: Form, diffedValues: ReactiveFieldValues): FieldValue[] {
    const elements: FormElements[] = [];
    for (const section of originalForm.sections) {
      if (section.organization) {
        elements.push(...[section.organization].flatMap(o => o.elements));
      }
    }
    return this.filterTransformElement(elements, diffedValues);
  }

  private transformApplicantFieldValues(originalForm: Form, diffedValues: ReactiveFieldValues): ApplicantFieldValue[] {

    const key = Object.keys(diffedValues)[0];
    const splitKey = key.split("_")
    const changedId = parseInt(splitKey[1]);
    
    const result: ApplicantFieldValue[] = [];

    for (const section of originalForm.sections) {
      if (!section.applicants) continue;

      for (const applicant of section.applicants) {
        if (applicant.id !== changedId) continue;

        const values = this.filterTransformElement(applicant.elements, diffedValues);
        if (values.length) {
          result.push({ id: applicant.id, sectionId: section.key, self: applicant.self, fieldValues: values });
        }
      }
    }
    return result;
  }

  /**
   * Compute the differences between two reactive form objects and return an object which contains
   * the fields which are different with the current reactive forms value associated with the key.
   *
   * @param previousReactiveFieldValues
   * @param currentReactiveFieldValues
   * @private
   */
  private computeDiffFromPreviousFormAndCurrent(previousReactiveFieldValues: ReactiveFieldValues, currentReactiveFieldValues: ReactiveFieldValues): ReactiveFieldValues {
    const diff: any = {};
    for (const key of Object.keys(currentReactiveFieldValues)) {
      if (!this.deepEqual(previousReactiveFieldValues[key], currentReactiveFieldValues[key])) {
        diff[key] = currentReactiveFieldValues[key];
      }
    }
    return diff;
  }

  private deepEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) return false;

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
      if (!this.deepEqual(a[key], b[key])) return false;
    }
    return true;
  }


  /**
   * Transforms the reactive form key/values into an object used for the form save request.
   *
   * Uses the original form to determine if a field has selections, which needs to transform the
   * valueIds to include the id and name of the selection.
   *
   * FieldValue represents the fields in the SubmitRequest. FieldValue which can have `value` or `valueIds` as one of fields.
   * `value`: is associated with fields which have 1 value.
   * `valueIds`: is associated with fields can have multiple values.
   *
   * @param originalForm
   * @param reactiveFieldValues
   * @private
   */
  private transformFieldValues(originalForm: Form, reactiveFieldValues: ReactiveFieldValues): FieldValue[] {
    const elements = originalForm.sections.flatMap(section => section.elements);
    return this.filterTransformElement(elements, reactiveFieldValues);
  }

  private filterTransformElement(elements: FormElements[], reactiveFieldValues: ReactiveFieldValues): FieldValue[] {
    const keys = Object.keys(reactiveFieldValues).filter(k => reactiveFieldValues[k] !== null && reactiveFieldValues[k] !== undefined);
    const transformedKeys = keys.map(key => key.split("_"))
        .map(keys => keys[keys.length - 1])
    const result: FieldValue[] = [];

    for (const el of elements) {
      if (!transformedKeys.includes(el.key.toString())) continue;

      const values = this.fieldValue(el, reactiveFieldValues);
      if (Array.isArray(values)) {
        result.push(...values);
      } else {
        result.push({ fieldId: el.key, ...values });
      }
    }
    return result;
  }

  /**
   * Function to return an object containing the elements value based its template options
   * @param element
   * @param reactiveFieldValues
   * @private
   */
  private fieldValue(element: FormElements, reactiveFieldValues: ReactiveFieldValues) {
    const key = Object.keys(reactiveFieldValues)
        .filter(key => key.endsWith(element.key.toString()))[0]

    const value = reactiveFieldValues[key];
    const templateOptions = element.templateOptions;

    const selectValueIds = (element: FormElements): string[] => {
      switch (templateOptions.displayType) {
        case 0: return [value.toString()];
        case 2: return Object.entries(value).filter(([_, v]) => v).map(([k]) => k);
        default: return Array.isArray(value) ? value : [value];
      }
    }

    const findOption = (list: any[], id: string) => list.find(opt => opt.id.toString() === id);

    if ([130, 348, 336].includes(element.type)) {
      return {
        fieldId: element.key,
        prefix: findOption(templateOptions.prefixes, value?.prefix),
        suffix: findOption(templateOptions.suffixes, value?.suffix),
        firstName: value?.firstName,
        lastName: value?.lastName,
        middleName: value?.middleName,
        middleInitial: value?.middleInitial
      };
    }

    if ([1340, 170].includes(element.type)) return { companyName: value };
    if (element.type === 120) return { displayName: value };
    if (element.type === 346) return { valueEnabled: value?.[0] };

    if ([8, 405, 7, 206, 207].includes(element.type)) {
      return { uuId: value?.uuId, value: value?.value, fileId: value?.fileId, fieldId: element.key };
    }

    if ([140, 160].includes(element.type)) {
      return Object.entries(value || {}).map(([key, entry]: [string, any]) => {
        const [fieldId, labelId, labelName] = key.split("_");
        return {
          fieldId: parseInt(fieldId),
          typeLabel: { id: parseInt(labelId), name: labelName },
          ...(entry.email ? { email: entry.email } : {}),
          ...(entry.phone ? { phone: entry.phone } : {})
        };
      });
    }

    if (element.type === 150) {
      return Object.entries(value || {}).map(([key, entry]: [string, any]) => {
        const [fieldId, labelId, labelName, index] = key.split("_");
        const valOpts = templateOptions.values[parseInt(index)];
        const response: FieldValue = {
          fieldId: parseInt(fieldId),
          typeLabel: { id: parseInt(labelId), name: labelName },
        };

        if (entry.city) response.city = entry.city;
        if (entry.line1) response.line1 = entry.line1;
        if (entry.line2) response.line2 = entry.line2;
        if (entry.zip) response.zip = entry.zip;
        if ('countries' in valOpts && entry.countries) response.country = findOption(valOpts.countries, entry.countries);
        if ('states' in valOpts && entry.states) response.state = findOption(valOpts.states, entry.states);

        return response;
      });
    }

    if (templateOptions.selections) {
      const selectedIds = selectValueIds(element);
      const filtered = templateOptions.selections
        .filter(opt => selectedIds.includes(opt.id.toString()));

      if (templateOptions.displayType === 0 && !templateOptions.required) {
        return { valueIds: filtered.filter(opt => opt.id !== 0).map(opt => ({ id: opt.id, name: opt.name })) };
      }

      return { valueIds: filtered.map(opt => ({ id: opt.id, name: opt.name })) };
    }

    return { value };
  }
}
